import { cn } from "@/common/helpers/utils";
import { Tooltip, TooltipContent, TooltipProviderIfMissing, TooltipTrigger } from "@/components/ui/tooltip";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

export enum ButtonVariant {
  Filled = "filled",
  Outlined = "outlined",
  Ghost = "ghost",
  Link = "link",
  Input = "input",
}

export enum ButtonColorVariant {
  Primary = "primary",
  Secondary = "secondary",
  Success = "success",
  Warning = "warning",
  Error = "error",
}

export enum ButtonSize {
  Default = "default",
  Sm = "sm",
  Lg = "lg",
  Icon = "icon",
}

const buttonVariants = cva(
  cn(
    "cursor-pointer disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md",
    "text-sm font-medium transition-colors",
    "disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ),
  {
    variants: {
      variant: {
        [ButtonVariant.Filled]: "",
        [ButtonVariant.Outlined]: "",
        [ButtonVariant.Ghost]: "border border-transparent",
        [ButtonVariant.Link]: "underline-offset-4 hover:underline",
        [ButtonVariant.Input]:
          "border border-transparent bg-input-background text-foreground hover:bg-accent hover:text-accent-foreground",
      },
      colorVariant: {
        [ButtonColorVariant.Primary]: "",
        [ButtonColorVariant.Secondary]: "",
        [ButtonColorVariant.Success]: "",
        [ButtonColorVariant.Warning]: "",
        [ButtonColorVariant.Error]: "",
      },
      size: {
        [ButtonSize.Default]: "h-9 px-4 py-2",
        [ButtonSize.Sm]: "h-9 rounded-md px-3 text-xs",
        [ButtonSize.Lg]: "h-9 rounded-md px-8",
        [ButtonSize.Icon]: "h-9 w-9",
      },
    },
    compoundVariants: [
      {
        variant: ButtonVariant.Filled,
        colorVariant: ButtonColorVariant.Primary,
        class: "border border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:border-transparent",
      },
      {
        variant: ButtonVariant.Filled,
        colorVariant: ButtonColorVariant.Secondary,
        class: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      },
      {
        variant: ButtonVariant.Filled,
        colorVariant: ButtonColorVariant.Success,
        class: "border border-success bg-success text-success-foreground hover:bg-success/90 hover:border-transparent",
      },
      {
        variant: ButtonVariant.Filled,
        colorVariant: ButtonColorVariant.Warning,
        class: "border border-warning bg-warning text-warning-foreground hover:bg-warning/90 hover:border-transparent",
      },
      {
        variant: ButtonVariant.Filled,
        colorVariant: ButtonColorVariant.Error,
        class: "border border-error bg-error text-error-foreground hover:bg-error/90 hover:border-transparent",
      },
      {
        variant: ButtonVariant.Outlined,
        colorVariant: ButtonColorVariant.Primary,
        class: "border border-primary bg-transparent text-primary hover:bg-primary/10",
      },
      {
        variant: ButtonVariant.Outlined,
        colorVariant: ButtonColorVariant.Secondary,
        class: "border border-foreground/10 bg-transparent hover:bg-accent hover:text-accent-foreground",
      },
      {
        variant: ButtonVariant.Outlined,
        colorVariant: ButtonColorVariant.Success,
        class: "border border-success bg-transparent text-success hover:bg-success/10",
      },
      {
        variant: ButtonVariant.Outlined,
        colorVariant: ButtonColorVariant.Warning,
        class: "border border-warning bg-transparent text-warning hover:bg-warning/10",
      },
      {
        variant: ButtonVariant.Outlined,
        colorVariant: ButtonColorVariant.Error,
        class: "border border-error bg-transparent text-error hover:bg-error/10",
      },
      {
        variant: ButtonVariant.Ghost,
        colorVariant: ButtonColorVariant.Primary,
        class: "text-primary hover:bg-primary/10",
      },
      {
        variant: ButtonVariant.Ghost,
        colorVariant: ButtonColorVariant.Secondary,
        class: "hover:bg-accent hover:text-accent-foreground",
      },
      {
        variant: ButtonVariant.Ghost,
        colorVariant: ButtonColorVariant.Success,
        class: "text-success hover:bg-success/10",
      },
      {
        variant: ButtonVariant.Ghost,
        colorVariant: ButtonColorVariant.Warning,
        class: "text-warning hover:bg-warning/10",
      },
      {
        variant: ButtonVariant.Ghost,
        colorVariant: ButtonColorVariant.Error,
        class: "text-error hover:bg-error/10",
      },
      { variant: ButtonVariant.Link, colorVariant: ButtonColorVariant.Primary, class: "text-primary" },
      { variant: ButtonVariant.Link, colorVariant: ButtonColorVariant.Secondary, class: "text-secondary" },
      { variant: ButtonVariant.Link, colorVariant: ButtonColorVariant.Success, class: "text-success" },
      { variant: ButtonVariant.Link, colorVariant: ButtonColorVariant.Warning, class: "text-warning" },
      { variant: ButtonVariant.Link, colorVariant: ButtonColorVariant.Error, class: "text-error" },
    ],
    defaultVariants: {
      variant: ButtonVariant.Filled,
      colorVariant: ButtonColorVariant.Primary,
      size: ButtonSize.Default,
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "title">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  tooltip?: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
}

const Button = ({
  className,
  variant,
  colorVariant,
  size,
  asChild = false,
  tooltip,
  "aria-label": ariaLabel,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button";
  const button = (
    <Comp
      className={cn(buttonVariants({ variant, colorVariant, size, className }))}
      ref={ref}
      aria-label={ariaLabel ?? (typeof tooltip === "string" ? tooltip : undefined)}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  return (
    <TooltipProviderIfMissing>
      <Tooltip>
        <TooltipTrigger asChild>
          {props.disabled ? (
            <span
              className="inline-flex"
              tabIndex={0}
              aria-disabled="true"
              aria-label={ariaLabel ?? (typeof tooltip === "string" ? tooltip : undefined)}
            >
              {button}
            </span>
          ) : (
            button
          )}
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProviderIfMissing>
  );
};
Button.displayName = "Button";

export { Button, buttonVariants };
